import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Mascot from '../components/Mascot';
import StepCard from '../components/StepCard';
import PrismViz from '../components/PrismViz';
import FractionBar from '../components/FractionBar';
import NumberLineViz from '../components/NumberLineViz';
import TapeDiagramViz from '../components/TapeDiagramViz';
import AreaModelViz from '../components/AreaModelViz';
import FactorTreeViz from '../components/FactorTreeViz';
import PercentGridViz from '../components/PercentGridViz';
import CoordinatePlaneViz from '../components/CoordinatePlaneViz';
import ConfidenceButtons from '../components/ConfidenceButtons';
import ScaffoldStageView, { ScaffoldStage } from '../components/ScaffoldStage';
import ProblemStatement from '../components/ProblemStatement';
import VerificationBadge, { Verification } from '../components/VerificationBadge';
import PhotoModal from '../components/PhotoModal';
import LoadingProgress from '../components/LoadingProgress';
import AskBuddy from '../components/AskBuddy';
import RecheckButton from '../components/RecheckButton';
import { Confidence, InputType, bumpSession, logProblem, updateProblem } from '../lib/db';
import { syncSummaryToParent } from '../lib/sync';
import { findSection } from '../content/chapters';

interface Step {
  title: string;
  explanation: string;
  math?: string;
}

interface Solution {
  chapter: string | null;
  problem_number: string | null;
  topic: string;
  problem_text: string;
  scaffold?: ScaffoldStage[];
  visual: {
    type:
      | 'prism'
      | 'fraction_bar'
      | 'number_line'
      | 'tape_diagram'
      | 'area_model'
      | 'factor_tree'
      | 'percent_grid'
      | 'coordinate_plane'
      | null;
    data: any;
  } | null;
  final_answer: string;
  full_steps: Step[];
  encouragement: string;
  data?: number[];
  error?: string;
}

function getDeviceId() {
  let id = localStorage.getItem('mb_device_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('mb_device_id', id);
  }
  return id;
}

type Mode = 'socratic' | 'dump';

export default function Solve() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [solution, setSolution] = useState<Solution | null>(null);
  const [verification, setVerification] = useState<Verification | null>(null);
  // Opus 4.7 re-check override — if Grace triggers a re-check and Opus returns
  // a different answer, the final-answer card flips to show this instead.
  const [recheckedAnswer, setRecheckedAnswer] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<Confidence | null>(null);
  const [problemId] = useState(() => crypto.randomUUID());
  const [startedAt] = useState(Date.now());

  // Socratic state
  const [mode, setMode] = useState<Mode>('socratic');
  const [stageIndex, setStageIndex] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);

  const input = useMemo(() => {
    const raw = sessionStorage.getItem('mb_input');
    return raw
      ? (JSON.parse(raw) as {
          image?: string;
          text?: string;
          note?: string;
          source?: InputType;
          chapter?: string;
        })
      : null;
  }, []);

  // Guard against React 18 StrictMode double-mounting the effect in dev,
  // which would double-count against the daily rate limit.
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!input) {
      navigate('/');
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await fetch('/api/solve', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-device-id': getDeviceId(),
          },
          body: JSON.stringify({ image: input.image, text: input.text, note: input.note }),
        });
        const data = (await res.json()) as Solution;
        if (!res.ok || data.error) {
          setError(data?.error ?? "Something went wrong. Let's try again.");
          setLoading(false);
          return;
        }
        setSolution(data);
        // If scaffold missing or empty, fall back to dump mode silently.
        if (!data.scaffold || data.scaffold.length === 0) setMode('dump');
        setLoading(false);
        await logProblem({
          id: problemId,
          timestamp: Date.now(),
          chapter: data.chapter,
          topic: data.topic,
          input_type: (input.source as InputType) ?? 'type',
          hints_used: 0,
          confidence: null,
          duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        });

        // Kick off verification in the background — scaffold is already
        // showing. The badge shows a "Double-checking…" pending state until
        // this resolves (~8–15s depending on whether Opus escalates).
        fetch('/api/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            problem: { image: input.image, text: input.text, note: input.note },
            solution: data,
          }),
        })
          .then((r) => (r.ok ? (r.json() as Promise<Verification>) : null))
          .then((v) => {
            if (v) setVerification(v);
          })
          .catch(() => {
            // Verification failure doesn't block the scaffold — leave badge
            // in pending state; user can still work through the problem.
          });
      } catch (err: any) {
        setError(err?.message ?? 'network error');
        setLoading(false);
      }
    })();
  }, [input, navigate, problemId, startedAt]);

  const handleConfidence = async (c: Confidence) => {
    setConfidence(c);
    await updateProblem(problemId, {
      confidence: c,
      hints_used: hintsUsed,
      duration_seconds: Math.round((Date.now() - startedAt) / 1000),
    });
    await bumpSession(Math.max(1, Math.round((Date.now() - startedAt) / 60000)));
    syncSummaryToParent();
  };

  const advanceStage = () => {
    if (!solution?.scaffold) return;
    const next = stageIndex + 1;
    setWrongAttempts(0);
    if (next >= solution.scaffold.length) {
      setFinished(true);
    } else {
      setStageIndex(next);
    }
  };

  const skipToAnswer = () => {
    setMode('dump');
    setFinished(true);
    setHintsUsed((h) => h + 1);
  };

  if (loading) {
    return <LoadingProgress />;
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-5 pt-16 text-center space-y-4">
        <Mascot mood="thinking" size={100} />
        <h2 className="font-display text-2xl">Hmm — that one stumped me.</h2>
        <p className="text-ink/70">
          {error === 'daily_limit_reached'
            ? "We've hit today's limit. Try again tomorrow!"
            : error === 'off_topic'
              ? "I'm your math buddy! Ask me a math question and I'll help."
              : "Let's try a clearer photo or rephrase the question."}
        </p>
        <button onClick={() => navigate(-1)} className="btn-primary">Try again</button>
      </div>
    );
  }

  if (!solution) return null;

  const chapterMeta = findSection(solution.chapter);
  const scaffold = solution.scaffold ?? [];
  const currentStage = scaffold[stageIndex];
  const showSocratic = mode === 'socratic' && !finished && !!currentStage;
  const showDump = mode === 'dump' || finished;

  return (
    <div className="max-w-xl mx-auto relative">
      {/* Floating Ask Buddy — sticky at the top of the Solve screen so Grace
          can ask a question anytime, mid-scaffold or after. */}
      <div className="sticky top-0 z-30 px-5 pt-3 pb-2 safe-top bg-cream/90 backdrop-blur-sm">
        <AskBuddy
          deviceId={getDeviceId()}
          context={{
            problem_text: solution.problem_text,
            final_answer: solution.final_answer,
            chapter: solution.chapter,
            topic: solution.topic,
            scaffold_summary: solution.full_steps
              ?.slice(0, 6)
              .map((s, i) => `${i + 1}. ${s.title}: ${s.math || s.explanation}`)
              .join(' | '),
          }}
        />
      </div>

    <div className="px-5 pt-4 pb-8 space-y-5">
      <header className="flex items-center gap-3">
        <Mascot mood={finished ? 'celebrating' : 'thinking'} size={56} />
        <div className="flex-1">
          {(solution.problem_number || chapterMeta) && (
            <div className="flex items-center gap-2 text-xs text-ink/60 mb-1 flex-wrap">
              {solution.problem_number && (
                <span className="bg-violet/20 text-violet px-2 py-0.5 rounded-full font-bold">
                  {solution.problem_number}
                </span>
              )}
              {chapterMeta && (
                <span>
                  Chapter {chapterMeta.chapter.num} · {chapterMeta.section.id}
                </span>
              )}
            </div>
          )}
          <h1 className="font-display font-black text-xl leading-tight">{solution.topic}</h1>
        </div>
      </header>

      {input?.image && (
        <button
          onClick={() => setPhotoOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet/20 text-violet font-bold text-xs border border-violet/30 hover:bg-violet/30 transition"
        >
          <span>📷</span>
          <span>See the original problem</span>
        </button>
      )}

      {solution.problem_text && <ProblemStatement text={solution.problem_text} />}

      <PhotoModal
        src={input?.image ?? null}
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
      />

      {solution.visual?.type === 'prism' && (
        <PrismViz
          length={solution.visual.data?.length ?? 4}
          width={solution.visual.data?.width ?? 3}
          height={solution.visual.data?.height ?? 2}
        />
      )}
      {solution.visual?.type === 'fraction_bar' && (
        <FractionBar
          numerator={solution.visual.data?.numerator ?? 1}
          denominator={solution.visual.data?.denominator ?? 2}
          label={solution.visual.data?.label}
        />
      )}
      {solution.visual?.type === 'number_line' && (
        <NumberLineViz
          min={solution.visual.data?.min ?? -10}
          max={solution.visual.data?.max ?? 10}
          marks={solution.visual.data?.marks}
          highlights={solution.visual.data?.highlights ?? []}
        />
      )}
      {solution.visual?.type === 'tape_diagram' && (
        <TapeDiagramViz
          rows={solution.visual.data?.rows ?? []}
          caption={solution.visual.data?.caption}
        />
      )}
      {solution.visual?.type === 'area_model' && (
        <AreaModelViz
          shape={solution.visual.data?.shape ?? 'triangle'}
          base={solution.visual.data?.base ?? 8}
          height={solution.visual.data?.height ?? 5}
          base2={solution.visual.data?.base2}
          unit={solution.visual.data?.unit ?? 'in'}
        />
      )}
      {solution.visual?.type === 'factor_tree' && solution.visual.data?.tree && (
        <FactorTreeViz
          tree={solution.visual.data.tree}
          finalAnswer={solution.visual.data?.finalAnswer}
        />
      )}
      {solution.visual?.type === 'percent_grid' && (
        <PercentGridViz percent={solution.visual.data?.percent ?? 0} />
      )}
      {solution.visual?.type === 'coordinate_plane' && (
        <CoordinatePlaneViz
          points={solution.visual.data?.points ?? []}
          connectPoints={solution.visual.data?.connectPoints}
          xMin={solution.visual.data?.xMin}
          xMax={solution.visual.data?.xMax}
          yMin={solution.visual.data?.yMin}
          yMax={solution.visual.data?.yMax}
        />
      )}

      {showSocratic && currentStage && (
        <>
          <div className="flex items-center gap-2 text-xs text-ink/60">
            <span className="font-semibold uppercase tracking-wide">Step</span>
            <div className="flex gap-1.5">
              {scaffold.map((_, i) => (
                <span
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i < stageIndex ? 'bg-sky' : i === stageIndex ? 'bg-coral' : 'bg-ink/10'
                  }`}
                />
              ))}
            </div>
          </div>
          <ScaffoldStageView
            stage={currentStage}
            wrongAttempts={wrongAttempts}
            onAdvance={advanceStage}
            onWrong={() => {
              setWrongAttempts((n) => n + 1);
              setHintsUsed((h) => h + 1);
            }}
            onReveal={() => setHintsUsed((h) => h + 1)}
          />
          <div className="text-center">
            <button
              onClick={skipToAnswer}
              className="text-sm text-ink/50 underline underline-offset-4"
            >
              just show me the answer →
            </button>
          </div>
        </>
      )}

      {showDump && (
        <>
          <div className="space-y-3">
            {solution.full_steps.map((s, i) => (
              <StepCard key={i} index={i} title={s.title} explanation={s.explanation} math={s.math} />
            ))}
          </div>

          {/* Hide the final-answer card when verification flags multiple disagreements.
              Pedagogical principle: don't give Grace a possibly-wrong number to memorize.
              Consensus (one alternate with 2+ votes): show the consensus INSTEAD. */}
          {(() => {
            const verdict = verification?.verdict;
            const hasConsensus = !!verification?.consensus;
            const isFlagged = verdict === 'flagged';

            if (isFlagged && !hasConsensus) {
              // No answer we can confidently show. Lead with Socratic work-together.
              return (
                <div className="card p-5 bg-violet/10 space-y-2">
                  <div className="font-display font-black text-lg text-violet">
                    🤔 Let's figure this out together
                  </div>
                  <div className="text-sm text-ink/80">
                    My checkers couldn't agree on this one. Work through the steps
                    above carefully, or ask a grownup. Don't write down a final
                    answer until you're sure.
                  </div>
                </div>
              );
            }

            const displayedAnswer =
              recheckedAnswer ??
              (hasConsensus ? verification!.consensus!.alternate : solution.final_answer);
            return (
              <div className="card p-5 bg-sky/10">
                <div className="text-xs text-sky font-semibold mb-1 flex items-center gap-2">
                  Final answer
                  {recheckedAnswer ? (
                    <span title="Updated by Opus 4.7 re-check">🔍</span>
                  ) : (
                    <>
                      {verdict === 'textbook_verified' && (
                        <span title="Matches Big Ideas Math answer key">📖</span>
                      )}
                      {verdict === 'verified' && (
                        <span className="text-sky" title="Independently verified">✓</span>
                      )}
                      {hasConsensus && (
                        <span className="text-ink/70" title="Checkers found a different answer">💡</span>
                      )}
                      {verdict === 'uncertain' && (
                        <span className="text-ink/70" title="Worth double-checking">!</span>
                      )}
                      {!verification && (
                        <span className="text-ink/40 text-xs font-normal italic">checking…</span>
                      )}
                    </>
                  )}
                </div>
                <div className="font-display text-2xl">{displayedAnswer}</div>
                {recheckedAnswer ? (
                  <div className="text-xs text-ink/60 mt-1 italic">
                    Updated by Opus 4.7 re-check
                  </div>
                ) : hasConsensus ? (
                  <div className="text-xs text-ink/60 mt-1 italic">
                    Updated from what Claude first wrote — see below
                  </div>
                ) : null}
                <RecheckButton
                  problem={{ image: input?.image, text: input?.text, note: input?.note }}
                  currentAnswer={displayedAnswer}
                  chapter={solution.chapter}
                  topic={solution.topic}
                  deviceId={getDeviceId()}
                  onAnswerUpdated={(a) => setRecheckedAnswer(a)}
                />
              </div>
            );
          })()}

          <VerificationBadge
            verification={verification}
            finalAnswer={solution.final_answer}
            chapterTitle={chapterMeta?.section.title}
          />

          <div className="card p-4 bg-violet/10 text-ink">
            <span className="mr-2">💬</span>
            {solution.encouragement}
          </div>

          <ConfidenceButtons value={confidence} onChange={handleConfidence} />

          <button onClick={() => navigate('/')} className="btn-ghost w-full">
            Done — back home
          </button>
        </>
      )}
    </div>
    </div>
  );
}
